import { inject, Injectable } from '@angular/core';
import { BadgeConfig, GroupHealth, MilestoneGroup } from '@models';
import { CommonFunctionsService } from '@services';

@Injectable({
  providedIn: 'root'
})
export class GroupTooltipService {
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);

  buildHTML(group: MilestoneGroup): string {
    return `
      <div class="study-gantt-chart-tooltip-container">
        <h3 class="study-gantt-chart-tooltip-title">${this.commonFunctionsService.toSentenceCase(group.name)}</h3>
        <div class="study-gantt-chart-tooltip-contents">
          ${['Site Activation', 'Subject Enrollment'].includes(group.name) ? this.getGroupHealthHTML(group.health) : ''}
          <p class="study-gantt-chart-tooltip-item">
            <span class="study-gantt-chart-tooltip-label">Start date</span>
            <span class="study-gantt-chart-tooltip-value">Baseline: ${this.commonFunctionsService.formatDate(group.startDates.baseline)}</span>
            <span class="study-gantt-chart-tooltip-value">Planned: ${this.commonFunctionsService.formatDate(group.startDates.planned)}</span>
            <span class="study-gantt-chart-tooltip-value">Actual: ${this.commonFunctionsService.formatDate(group.startDates.actual)}</span>
          </p>
          <p class="study-gantt-chart-tooltip-item">
            <span class="study-gantt-chart-tooltip-label">End date</span>
            <span class="study-gantt-chart-tooltip-value">Baseline: ${this.commonFunctionsService.formatDate(group.endDates.baseline)}</span>
            <span class="study-gantt-chart-tooltip-value">Planned: ${this.commonFunctionsService.formatDate(group.endDates.planned)}</span>
            <span class="study-gantt-chart-tooltip-value">Actual: ${this.commonFunctionsService.formatDate(group.endDates.actual)}</span>
          </p>
          <p class="study-gantt-chart-tooltip-item">
            <span class="study-gantt-chart-tooltip-value">
              <span class="study-gantt-chart-tooltip-highlight-value">Total duration: </span> 
              ${this.getFormattedTotalDuration(group.totalDuration)}
            </span>
          </p>
        </div>
      </div>
    `;
  }

  getGroupHealthHTML(groupHealth: GroupHealth) {
    const badge = this.getGroupBadge(groupHealth);
    const statusIcon =
      badge.icon != '' ? `<img slot="icon" src="${badge.icon}" alt="${badge.label} icon"/>` : '';

    return `
        
     <div class="study-gantt-chart-tooltip-status-item">
        <span class="study-gantt-chart-tooltip-label">Status:</span>
        <core-badge 
          color="${badge.textColor}" 
          backgroundcolor="${badge.backgroundColor ?? ''}" 
          label="${badge.label}" 
          showicon="${badge.icon != ''}"
        >
          ${statusIcon}
        </core-badge>
      </div>`;
  }
  //TODO: Health status values will be updated in the future
  private getGroupBadge(groupHealth: GroupHealth): BadgeConfig {
    const badgeMap: Record<string, BadgeConfig> = {
      COMPLETED: {
        label: 'Completed',
        textColor: 'blue',
        backgroundColor: null,
        icon: 'assets/icons/statuses/completed-blue-badge-bold.svg',
        iconColor: null
      },
      IN_PROGRESS: {
        label: 'In progress',
        textColor: '#004E61',
        backgroundColor: '#D9FBE2',
        icon: 'assets/icons/statuses/on-track-green-badge-bold.svg',
        iconColor: null
      },
      IN_PROGRESS_PAST_DUE: {
        label: 'Past due',
        textColor: 'red',
        backgroundColor: null,
        icon: 'assets/icons/statuses/past-due-red-badge-bold.svg',
        iconColor: null
      },
      NOT_STARTED: {
        label: 'Upcoming',
        textColor: 'gray',
        backgroundColor: null,
        icon: 'assets/icons/statuses/upcoming-black-badge-bold.svg',
        iconColor: null
      }
    };

    return badgeMap[groupHealth] ?? badgeMap['NOT_STARTED'];
  }

  private getFormattedTotalDuration(totalDuration: number | null): string {
    if (totalDuration === null) {
      return '--';
    }
    const daysLabel = totalDuration === 1 ? 'day' : 'days';

    return `${totalDuration} ${daysLabel}`;
  }
}
